import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Payment as PaymentIcon,
  Storage as StorageIcon,
  Api as ApiIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'

import SettingsTab from '../../components/Settings/SettingsTab'
import SecuritySettings from '../../components/Settings/SecuritySettings'
import NotificationSettings from '../../components/Settings/NotificationSettings'
import PaymentSettings from '../../components/Settings/PaymentSettings'
import SystemConfiguration from '../../components/Settings/SystemConfiguration'
import ApiSettings from '../../components/Settings/ApiSettings'
import { useSystemSettings } from '../../hooks/useSystemSettings'
import { useSystemInfo } from '../../hooks/useSettings'
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
)

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null })

  const {
    data: settingsData,
    isLoading,
    error,
    refetch,
  } = useSystemSettings()

  const {
    data: systemInfo,
    isLoading: systemInfoLoading,
  } = useSystemInfo()

  const handleTabChange = (event, newValue) => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        open: true,
        action: () => {
          setActiveTab(newValue)
          setHasUnsavedChanges(false)
        }
      })
    } else {
      setActiveTab(newValue)
    }
  }

  const handleSettingsChange = () => {
    setHasUnsavedChanges(true)
  }

  const handleSaveSettings = async () => {
    try {
      // Save settings logic would go here
      setHasUnsavedChanges(false)
      // toast.success('Ayarlar başarıyla kaydedildi')
    } catch (error) {
      console.error('Settings save error:', error)
    }
  }

  const tabs = [
    {
      label: 'Genel',
      icon: <SettingsIcon />,
      description: 'Genel sistem ayarları'
    },
    {
      label: 'Güvenlik',
      icon: <SecurityIcon />,
      description: 'Güvenlik ve erişim ayarları'
    },
    {
      label: 'Bildirimler',
      icon: <NotificationsIcon />,
      description: 'Bildirim ve uyarı ayarları'
    },
    {
      label: 'Ödeme',
      icon: <PaymentIcon />,
      description: 'Ödeme sistemi ayarları'
    },
    {
      label: 'API',
      icon: <ApiIcon />,
      description: 'API konfigürasyonu'
    },
    {
      label: 'Sistem',
      icon: <StorageIcon />,
      description: 'Sistem konfigürasyonu'
    },
  ]

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Sistem Ayarları
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Admin paneli ve sistem yapılandırmasını yönetin
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              Yenile
            </Button>
            
            {hasUnsavedChanges && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                color="primary"
              >
                Değişiklikleri Kaydet
              </Button>
            )}
          </Box>
        </Box>

        {hasUnsavedChanges && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Kaydedilmemiş değişiklikleriniz var. Sayfadan ayrılmadan önce kaydetmeyi unutmayın.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Settings Content */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2 }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                sx={{ minHeight: 72 }}
              />
            ))}
          </Tabs>
        </Box>

        <CardContent>
          {/* General Settings Tab */}
          <TabPanel value={activeTab} index={0}>
            <SettingsTab
              title="Genel Ayarlar"
              description="Temel sistem ayarlarını yapılandırın"
              settings={settingsData?.general}
              onChange={handleSettingsChange}
              isLoading={isLoading}
            />
          </TabPanel>

          {/* Security Settings Tab */}
          <TabPanel value={activeTab} index={1}>
            <SecuritySettings
              settings={settingsData?.security}
              onChange={handleSettingsChange}
              isLoading={isLoading}
            />
          </TabPanel>

          {/* Notification Settings Tab */}
          <TabPanel value={activeTab} index={2}>
            <NotificationSettings
              settings={settingsData?.notifications}
              onChange={handleSettingsChange}
              isLoading={isLoading}
            />
          </TabPanel>

          {/* Payment Settings Tab */}
          <TabPanel value={activeTab} index={3}>
            <PaymentSettings
              settings={settingsData?.payment}
              onChange={handleSettingsChange}
              isLoading={isLoading}
            />
          </TabPanel>

          {/* API Settings Tab */}
          <TabPanel value={activeTab} index={4}>
            <ApiSettings
              settings={settingsData?.api}
              onChange={handleSettingsChange}
              isLoading={isLoading}
            />
          </TabPanel>

          {/* System Configuration Tab */}
          <TabPanel value={activeTab} index={5}>
            <SystemConfiguration
              settings={settingsData?.system}
              onChange={handleSettingsChange}
              isLoading={isLoading}
            />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Kaydedilmemiş Değişiklikler</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography>
            Bu sekmede kaydedilmemiş değişiklikleriniz var. 
            Diğer sekmeye geçerseniz bu değişiklikler kaybolacak.
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            Devam etmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, action: null })}
          >
            İptal
          </Button>
          <Button 
            onClick={handleSaveSettings}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Kaydet ve Devam Et
          </Button>
          <Button 
            onClick={() => {
              confirmDialog.action?.()
              setConfirmDialog({ open: false, action: null })
            }}
            color="warning"
          >
            Kaydetmeden Devam Et
          </Button>
        </DialogActions>
      </Dialog>

      {/* System Status Footer */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Sistem Durumu
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color={systemInfo?.status === 'healthy' ? 'success' : 'warning'} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Sistem Sagligi
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {systemInfoLoading ? 'Yukleniyor...' : (systemInfo?.statusMessage || '-')}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <InfoIcon color="info" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Versiyon
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {systemInfoLoading ? 'Yukleniyor...' : (
                      systemInfo?.version
                        ? `v${systemInfo.version}${systemInfo.buildNumber ? ` (Build ${systemInfo.buildNumber})` : ''}`
                        : '-'
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Son Guncelleme
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {systemInfoLoading ? 'Yukleniyor...' : (
                      systemInfo?.lastDeployment
                        ? formatDistanceToNow(new Date(systemInfo.lastDeployment), { addSuffix: true, locale: tr })
                        : '-'
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

export default SystemSettings